import { apiClient, QueryParams } from './apiClient';

export type ProductivityContentType = 'ALL' | 'MOVIE' | 'EPISODE';
export type QuotaMetricType = 'FILES_UPLOADED' | 'CONTENT_COMPLETED';
export type QuotaRecurrenceType = 'ONE_TIME' | 'WEEKLY' | 'MONTHLY';

export interface ProductivityFilters extends QueryParams {
  from: string;
  to: string;
  userId?: number;
  contentType?: ProductivityContentType;
}

export interface ProductivitySummary {
  filesUploaded: number;
  contentCompleted: number;
  validFiles: number;
  invalidFiles: number;
  uploadedBytes: number;
  activeContributors: number;
}

export interface UserProductivity {
  userId: number;
  username: string;
  fullName: string;
  contentType: Exclude<ProductivityContentType, 'ALL'>;
  filesUploaded: number;
  contentCompleted: number;
  validFiles: number;
  invalidFiles: number;
  uploadedBytes: number;
  goals?: ProductivityGoalProgress[];
}

export interface ProductivityGoalProgress {
  goalId: number;
  recurrenceType: QuotaRecurrenceType;
  contentType: ProductivityContentType;
  metricType: QuotaMetricType;
  targetQuantity: number;
  achievedQuantity: number;
  progressPercentage: number;
}

export interface DailyProductivity {
  date: string;
  contentType: Exclude<ProductivityContentType, 'ALL'>;
  filesUploaded: number;
  contentCompleted: number;
}

export interface ContentProductivityReport {
  from: string;
  to: string;
  summary: ProductivitySummary;
  users: UserProductivity[];
  dailyActivity: DailyProductivity[];
}

export type ProductivityDetailStatus =
  | 'ALL'
  | 'FILES_UPLOADED'
  | 'CONTENT_COMPLETED'
  | 'VALID'
  | 'INVALID';

export interface ProductivityDetailFilters extends QueryParams {
  from: string;
  to: string;
  userId: number;
  contentType: Exclude<ProductivityContentType, 'ALL'>;
  status: ProductivityDetailStatus;
  search?: string;
  _page?: number;
  _size?: number;
}

export interface ProductivityDetail {
  contentId: string;
  mediaLinkId?: string;
  contentType: Exclude<ProductivityContentType, 'ALL'>;
  title: string;
  parentTitle?: string;
  quality?: string;
  valid: boolean;
  active: boolean;
  completed: boolean;
  contentLength?: number;
  uploadedAt: string;
  editPath: string;
}

export interface ContentUploadQuota {
  id: number;
  userId: number;
  username?: string;
  fullName?: string;
  periodStart: string;
  periodEnd?: string;
  recurrenceType: QuotaRecurrenceType;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  contentType: ProductivityContentType;
  metricType: QuotaMetricType;
  targetQuantity: number;
  achievedQuantity: number;
  progressPercentage: number;
  notes?: string;
  createdDate: string;
}

export type SaveContentUploadQuota = Omit<
  ContentUploadQuota,
  'id' | 'username' | 'fullName' | 'createdDate' | 'achievedQuantity' | 'progressPercentage'
  | 'currentPeriodStart' | 'currentPeriodEnd'
>;

export interface ContentContributor {
  id: number;
  username: string;
  fullName: string;
}

const endpoint = '/reports/content-productivity';

export const contentProductivityService = {
  getReport: (filters: ProductivityFilters) =>
    apiClient.get<ContentProductivityReport>(endpoint, filters),

  getQuotas: (filters: Pick<ProductivityFilters, 'from' | 'to' | 'userId'>) =>
    apiClient.get<ContentUploadQuota[]>(`${endpoint}/quotas`, filters),

  getContributors: () =>
    apiClient.get<ContentContributor[]>(`${endpoint}/contributors`),

  getDetails: (filters: ProductivityDetailFilters) =>
    apiClient.getPaginated<ProductivityDetail>(`${endpoint}/details`, filters),

  createQuota: (quota: SaveContentUploadQuota) =>
    apiClient.post<ContentUploadQuota>(`${endpoint}/quotas`, quota),

  updateQuota: (id: number, quota: SaveContentUploadQuota) =>
    apiClient.put<ContentUploadQuota>(`${endpoint}/quotas/${id}`, quota),

  deleteQuota: (id: number) =>
    apiClient.delete<void>(`${endpoint}/quotas/${id}`),
};
